import type { ReactNode } from 'react'
import type { JournalPost } from '@/types'

import Image from 'next/image'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { formatPostDate } from '@/lib/journal'

interface JournalCardProps {
    post: JournalPost
    /** `featured` is the wide hero card, `default` the grid tile. */
    variant?: 'featured' | 'default'
    priority?: boolean
    className?: string
}

export const JournalCard = ({
    post,
    variant = 'default',
    priority = false,
    className
}: JournalCardProps): ReactNode => {
    const isFeatured = variant === 'featured'

    return (
        <article data-journal-card='' className={cn('h-full', className)}>
            <Link
                href={`/journal/${post.slug}`}
                className='group relative block h-full overflow-hidden rounded-2xl bg-shell focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink'
            >
                <div
                    className={cn(
                        'relative w-full',
                        isFeatured ? 'aspect-[16/10] md:aspect-[16/9]' : 'aspect-[4/3]'
                    )}
                >
                    <Image
                        src={post.coverImage}
                        alt={post.coverImageAlt}
                        fill
                        priority={priority}
                        sizes={isFeatured ? '100vw' : '(min-width: 768px) 50vw, 100vw'}
                        className='object-cover transition-transform duration-[1100ms] ease-nuvea group-hover:scale-[1.05]'
                    />

                    <span
                        aria-hidden='true'
                        className='absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/15 to-ink/10'
                    />
                </div>

                <span className='absolute top-4 left-4 md:top-5 md:left-5'>
                    <span
                        data-mdlfy-cms={`journal_posts:${post.id}:category`}
                        className='type-micro inline-block rounded-full bg-cream/90 px-3 py-1.5 text-ink backdrop-blur-sm'
                    >
                        {post.category}
                    </span>
                </span>

                <div className='absolute inset-x-0 bottom-0 flex flex-col gap-1 p-5 md:p-7'>
                    <p className='type-micro text-cream/80'>
                        {formatPostDate(post.publishedAt)}
                    </p>

                    <h3
                        data-mdlfy-cms={`journal_posts:${post.id}:title`}
                        className={cn(
                            'max-w-[18ch] text-cream',
                            isFeatured ? 'type-h1' : 'type-h3'
                        )}
                    >
                        {post.title}
                    </h3>
                </div>
            </Link>
        </article>
    )
}
